import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { AuthService } from '../services/auth.service';

/**
 * Route guard factory. `data: { permission: 'student.read' }` on the route, or
 * pass codes directly: `canActivate: [requirePermission('school.create')]`.
 *
 * This is a convenience for the user, not a security boundary - the API
 * re-checks every permission server side.
 */
export function requirePermission(...codes: string[]): CanActivateFn {
  return (route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isAuthenticated()) {
      return router.createUrlTree(['/login']);
    }

    const required = codes.length > 0 ? codes : [route.data?.['permission']].filter(Boolean);
    const granted = required.every((code: string) => auth.can(code));
    if (granted) {
      return true;
    }
    // Bouncing to a home that is itself this refused page would loop.
    const home = auth.homeRoute();
    return router.parseUrl(state.url.split('?')[0] === home ? '/profile' : home);
  };
}

/**
 * As `requirePermission`, but satisfied by any one of the codes — for a screen
 * two portals share under different rights, like the timetable: a teacher
 * reads the school's (`timetable.read`), a family their own (`self.timetable`).
 */
export function requireAnyPermission(...codes: string[]): CanActivateFn {
  return (route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isAuthenticated()) {
      return router.createUrlTree(['/login']);
    }
    if (codes.some(code => auth.can(code))) {
      return true;
    }
    const home = auth.homeRoute();
    return router.parseUrl(state.url.split('?')[0] === home ? '/profile' : home);
  };
}
